import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Table, Tag, Empty, Spin, Typography, Button } from 'antd';
import { CheckCircleFilled, MinusCircleOutlined, FullscreenOutlined, EditOutlined } from '@ant-design/icons';
import { useHomeAssignmentActions } from '_actions/homeAssignment.actions';
import { MarkupEditor } from './MarkupEditor';
import dayjs from '_helpers/datetime';

const { Text } = Typography;

export { SubmissionRoster };

// Inline preview of a single submitted file, served behind auth via /api/photos/:fileId.
function FilePreview({ file }) {
    const url = `/api/photos/${file.drive_file_id}`;
    const mime = file.mime_type || '';
    let body;
    if (mime.startsWith('image/')) {
        body = <img src={url} alt={file.original_name} style={{ maxWidth: '100%', maxHeight: 360, display: 'block' }} />;
    } else if (mime === 'application/pdf') {
        body = <iframe src={url} title={file.original_name} style={{ width: '100%', height: 480, border: '1px solid #eee' }} />;
    } else if (mime.startsWith('audio/')) {
        body = <audio controls src={url} style={{ width: '100%' }} />;
    } else {
        body = <a href={url} target="_blank" rel="noopener noreferrer">{file.original_name || 'Tải tệp'}</a>;
    }
    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{file.original_name}</Text>
                {/* Full-size view in a new tab — same navigation as the assignment attachments. */}
                <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    <FullscreenOutlined /> Xem toàn màn hình
                </a>
            </div>
            {body}
        </div>
    );
}

function ExpandedSubmission({ row }) {
    if (!row.files || row.files.length === 0) {
        return <Text type="secondary">Chưa có tệp nào.</Text>;
    }
    return (
        <div>
            {row.files.map((f, i) => <FilePreview key={f.drive_file_id || i} file={f} />)}
        </div>
    );
}

function SubmissionRoster({ visible, onClose, classId, assignment }) {
    const actions = useHomeAssignmentActions();
    const [loading, setLoading] = useState(false);
    const [roster, setRoster] = useState([]);
    const [editor, setEditor] = useState({ visible: false, row: null });

    const load = useCallback(() => {
        if (!visible || !assignment) return Promise.resolve();
        setLoading(true);
        return actions.getSubmissions(classId, assignment._id || assignment.id)
            .then((data) => setRoster(Array.isArray(data) ? data : []))
            .catch(() => setRoster([]))
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, classId, assignment]);

    useEffect(() => {
        if (visible && assignment) { load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, classId, assignment]);

    const columns = [
        {
            title: 'Học sinh',
            dataIndex: 'name',
            key: 'name',
            render: (name, r) => (
                <span>{name} <Text type="secondary" style={{ fontSize: 12 }}>({r.student_id})</Text></span>
            ),
        },
        {
            title: 'Trạng thái',
            dataIndex: 'submitted',
            key: 'submitted',
            width: 130,
            render: (submitted) => submitted
                ? <Tag color="green" icon={<CheckCircleFilled />}>Đã nộp</Tag>
                : <Tag icon={<MinusCircleOutlined />}>Chưa nộp</Tag>,
        },
        {
            title: 'Số tệp',
            key: 'count',
            width: 90,
            render: (_, r) => (r.files ? r.files.length : 0),
        },
        {
            title: 'Nộp lúc',
            dataIndex: 'last_submitted_at',
            key: 'last_submitted_at',
            render: (t) => t ? dayjs(t).format('DD/MM/YYYY HH:mm') : '—',
        },
        {
            title: 'Điểm',
            key: 'grade',
            width: 170,
            render: (_, r) => {
                const rv = r.review;
                if (!rv || (rv.grade == null && !rv.comment)) return <Text type="secondary">—</Text>;
                return (
                    <span>
                        {rv.grade != null
                            ? <Tag color="blue" style={{ fontWeight: 600 }}>{rv.grade}</Tag>
                            : <Tag>Đã chấm</Tag>}
                        {rv.comment
                            ? <Text type="secondary" style={{ fontSize: 12, display: 'block', maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={rv.comment}>{rv.comment}</Text>
                            : null}
                    </span>
                );
            },
        },
        {
            title: '',
            key: 'action',
            width: 110,
            render: (_, r) => r.submitted
                ? (
                    <Button size="small" icon={<EditOutlined />} onClick={() => setEditor({ visible: true, row: r })}>
                        Chấm bài
                    </Button>
                )
                : null,
        },
    ];

    const submittedCount = roster.filter(r => r.submitted).length;

    return (
        <Modal
            title={`Bài nộp — ${assignment ? assignment.title : ''}`}
            visible={visible}
            onCancel={onClose}
            footer={null}
            width={760}
        >
            {loading
                ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                : (
                    <>
                        <div style={{ marginBottom: 12 }}>
                            <Tag color="blue">Đã nộp: {submittedCount}/{roster.length}</Tag>
                        </div>
                        <Table
                            size="small"
                            rowKey={(r) => r.student_id}
                            columns={columns}
                            dataSource={roster}
                            pagination={false}
                            locale={{ emptyText: <Empty description="Không có học sinh" /> }}
                            expandable={{
                                expandedRowRender: (r) => <ExpandedSubmission row={r} />,
                                rowExpandable: (r) => r.submitted && r.files && r.files.length > 0,
                            }}
                        />
                    </>
                )}

            <MarkupEditor
                visible={editor.visible}
                onClose={() => setEditor({ visible: false, row: null })}
                classId={classId}
                assignment={assignment}
                row={editor.row}
                onSaved={load}
            />
        </Modal>
    );
}
